import { useEffect, useState } from 'react';
import type { ActivityEvent, ActivityOrigin, ActivityResponse } from '../api/client.js';
import { fetchActivity } from '../api/client.js';
import { Stamp } from '../components/Stamp.js';
import { useDictionary } from '../i18n/context.js';

// ---------------------------------------------------------------------------
// Activity (UI structure spec 15): an immutable, searchable record of
// UI/MCP/scheduler/publisher/system actions. Each row shows timestamp
// (browser-local, via Stamp -- 21.1), project, actor/origin, action,
// target, and outcome (15.2). There is no delete or edit action, per that
// same section -- this screen renders no such control.
//
// 15.2's other rule: "The UI does not label every MCP action as 'AI'
// unless the authenticated client actually provides that identity."
// `origin` renders as the raw wire value ('mcp', not a translated or
// dramatized label), mirroring review.tsx's own recent-activity table
// (`{e.origin}`, with `origin--ai` only as a CSS accent, never as text).
// `actor` -- the client identity when the Worker resolved one, else the
// acting id (apps/worker/src/api/activity.ts's own header comment) -- is
// shown exactly as received; an empty value renders nothing rather than a
// client-side placeholder like "AI" or "MCP client".
//
// Filters: only `origin` is wired up here (10.1's sibling section, 15.1,
// lists project/campaign/category/date/result too) -- enough to prove out
// distinct "no records at all" vs "no records match this filter" empty
// states (20.2) without the full filter surface, which can follow once
// there is enough activity data to filter meaningfully.
// ---------------------------------------------------------------------------

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: ActivityResponse };

const ORIGIN_OPTIONS: ActivityOrigin[] = ['ui', 'mcp', 'scheduler', 'publisher', 'system'];

const ActivityRowView = ({ event }: { event: ActivityEvent }): React.JSX.Element => {
  const dict = useDictionary();

  return (
    <tr data-testid={`activity-row-${event.id}`}>
      <td><Stamp instant={event.at} compact /></td>
      <td>{event.projectName ?? dict.ui.activity.noProject}</td>
      <td>
        <span className={event.origin === 'mcp' ? 'origin origin--ai' : 'origin origin--system'}>{event.origin}</span>
        {event.actor && <span className="mono-meta">{event.actor}</span>}
      </td>
      <td>{event.action}</td>
      <td>{event.target}</td>
      <td>
        <span className={event.result === 'ok' ? 'chip chip--done' : 'chip chip--danger'}>
          {event.result === 'ok' ? dict.ui.activity.resultOk : dict.ui.activity.resultFailed}
        </span>
      </td>
    </tr>
  );
};

export const ActivityRoute = (): React.JSX.Element => {
  const dict = useDictionary();
  const [origin, setOrigin] = useState<ActivityOrigin | ''>('');
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = (): void => {
    setState({ status: 'loading' });
    fetchActivity(origin === '' ? undefined : { origin })
      .then((data) => setState({ status: 'ready', data }))
      .catch(() => setState({ status: 'error' }));
  };

  useEffect(load, [origin]);

  const filterActive = origin !== '';

  return (
    <div className="stack" style={{ gap: 'var(--sp-5)' }}>
      <div className="between">
        <h1 className="t-title">{dict.ui.activity.title}</h1>
        <div className="field" style={{ maxWidth: 200 }}>
          <label className="field__label" htmlFor="activity-origin-filter">{dict.ui.activity.filterOriginLabel}</label>
          <select
            id="activity-origin-filter"
            className="select"
            value={origin}
            onChange={(e) => { setOrigin(e.target.value as ActivityOrigin | ''); }}
          >
            <option value="">{dict.ui.activity.filterOriginAll}</option>
            {ORIGIN_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>

      {state.status === 'loading' && <p className="t-meta">{dict.ui.activity.loading}</p>}

      {state.status === 'error' && (
        <div className="alert alert--danger">
          <div className="stack-1">
            <div className="alert__title">{dict.ui.activity.loadFailed}</div>
            <button type="button" className="btn" onClick={load}>{dict.ui.activity.retry}</button>
          </div>
        </div>
      )}

      {state.status === 'ready' && (
        <div className="panel">
          <div className="panel__body panel__body--flush">
            {state.data.events.length === 0
              ? (
                <div className="empty">
                  <div className="empty__title">{filterActive ? dict.ui.activity.noMatch : dict.ui.activity.noEvents}</div>
                  <div className="empty__body">{filterActive ? dict.ui.activity.noMatchBody : dict.ui.activity.noEventsBody}</div>
                </div>
              )
              : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>{dict.ui.activity.timeColumn}</th>
                      <th>{dict.ui.activity.projectColumn}</th>
                      <th>{dict.ui.activity.originColumn}</th>
                      <th>{dict.ui.activity.actionColumn}</th>
                      <th>{dict.ui.activity.targetColumn}</th>
                      <th>{dict.ui.activity.resultColumn}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.events.map((event) => <ActivityRowView key={event.id} event={event} />)}
                  </tbody>
                </table>
              )}
          </div>
        </div>
      )}
    </div>
  );
};
